import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MAT_MOMENT_DATE_FORMATS, MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_CARD_CONFIG } from '@angular/material/card';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { AuthServiceProvider } from '@app/core/providers/auth.provider';
import { GlobalErrorHandler } from '@app/core/providers/error.handler';
import { MarkdownWebApplicationConfigurationService } from '@app/core/services/markdown-web-application-configuration.service';
import { MultipleFileTranslateHttpLoader } from '@app/core/translate/MultipleFileTranslateHttpLoader';
import { CustomPaginator } from '@app/core/translate/custom-paginator';
import { TranslateService } from '@app/shared/services/translate.service';
import { TranslateService as NgxTranslateService, provideTranslateService, TranslateLoader, TranslateParser } from '@ngx-translate/core';
import { OAuthModule, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { CookieService } from 'ngx-cookie-service';
import { HIGHLIGHT_OPTIONS, HighlightModule } from 'ngx-highlightjs';
import { MARKED_OPTIONS, MarkedOptions, provideMarkdown } from 'ngx-markdown';
import { ApiModule as StsApiModule, Configuration as StsConfiguration } from '../../target/openapi-sts-api-typescript';
import { ApiModule as StsApplicationApiModule, Configuration as StsApplicationConfiguration } from '../../target/openapi-sts-application-api-typescript';
import { BASE_PATH as T_BASE_PATH, ApiModule as TApiModule, Configuration as TConfiguration } from '../../target/openapi-t-typescript';
import { ApiModule, BASE_PATH, Configuration } from '../../target/openapi-typescript';
import { environment } from '../environments/environment';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { HttpUnauthorizedInterceptor } from './core/interceptors/http-unauthorized.interceptor';
import MarkdownRenderer from './core/markdown/markdown-renderer';
import * as MaterialModules from './shared/material'; // => Import all Material/CDK modules

export function markedOptionsFactory(markdownWebApplicationConfigurationService: MarkdownWebApplicationConfigurationService): MarkedOptions<string, string> {
  const renderer = new MarkdownRenderer(markdownWebApplicationConfigurationService);
  // ngx-markdown feeds these options into marked.use(), which expects a RendererObject
  // despite the Renderer-typed option, hence the cast
  return {
    ...renderer.getMarkedExtension(),
    gfm: true,
    breaks: false,
    pedantic: false
  } as unknown as MarkedOptions<string, string>;
}

export function authProviderFactory(provider: AuthServiceProvider) {
  return () => provider.load();
}

// i18n requests must bypass the DI interceptor chain (auth/error interceptors depend on
// TranslateService, which loads i18n eagerly at construction — going through them would
// create a circular dependency at bootstrap), hence a raw HttpClient on HttpBackend.
export function HttpLoaderFactory(httpBackend: HttpBackend) {
  return new MultipleFileTranslateHttpLoader(new HttpClient(httpBackend), [
    { prefix: './assets/i18n/', suffix: '.json', renameIndicator: true },
    { prefix: `${environment.API_BASE_PATH}/global/i18n/`, suffix: '.json', renameIndicator: false }
  ]);
}

export const appConfig = {
  imports: [
    HttpClientModule,
    OAuthModule.forRoot(),
    HighlightModule,
    ApiModule,
    StsApiModule,
    StsApplicationApiModule,
    TApiModule,
    MatMomentDateModule,

    // Spread all Material/CDK modules here
    ...Object.values(MaterialModules)
  ],
  providers: [
    provideTranslateService({
      fallbackLang: 'en-GB',
      loader: { provide: TranslateLoader, useFactory: HttpLoaderFactory, deps: [HttpBackend] }
    }),
    // Language state (currentLang, onLangChange) is per service instance in ngx-translate 18,
    // no longer on the shared store. Alias the library token to the app's TranslateService so
    // TranslatePipe/TrackAndTranslatePipe and app code share one instance.
    { provide: NgxTranslateService, useExisting: TranslateService },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: MAT_SELECT_CONFIG, useValue: { typeaheadDebounceInterval: 750 } },
    { provide: MAT_CARD_CONFIG, useValue: { appearance: 'raised' } },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false } },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: MatPaginatorIntl, useClass: CustomPaginator, deps: [TranslateService, TranslateParser] },
    { provide: BASE_PATH, useValue: environment.API_BASE_PATH },
    { provide: T_BASE_PATH, useValue: environment.T_API_BASE_PATH },
    {
      provide: StsConfiguration,
      useFactory: (oauthService: OAuthService) => new StsConfiguration({ accessToken: oauthService.getIdToken.bind(oauthService) }),
      deps: [OAuthService],
      multi: false
    },
    {
      provide: StsApplicationConfiguration,
      useFactory: (_oauthService: OAuthService) => new StsApplicationConfiguration({}),
      deps: [OAuthService],
      multi: false
    },
    {
      provide: Configuration,
      useFactory: (authStorage: OAuthStorage) =>
        new Configuration({
          accessToken: () => authStorage.getItem('api_access_token') ?? ''
        }),
      deps: [OAuthStorage],
      multi: false
    },
    {
      provide: TConfiguration,
      useFactory: (authStorage: OAuthStorage) =>
        new TConfiguration({
          accessToken: () => authStorage.getItem('api_access_token') ?? ''
        }),
      deps: [OAuthStorage],
      multi: false
    },
    {
      provide: HIGHLIGHT_OPTIONS,
      useValue: {
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        lineNumbersLoader: () => import('ngx-highlightjs/line-numbers'),
        languages: {
          xml: () => import('highlight.js/lib/languages/xml')
        }
      }
    },
    provideMarkdown({
      loader: HttpClient,
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory,
        deps: [MarkdownWebApplicationConfigurationService]
      }
    }),
    AuthServiceProvider,
    provideAppInitializer(() => {
      const initializerFn = authProviderFactory(inject(AuthServiceProvider));
      return initializerFn();
    }),
    { provide: OAuthStorage, useFactory: () => sessionStorage },
    { provide: HTTP_INTERCEPTORS, useClass: HttpUnauthorizedInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    CookieService
  ]
};
